/**
 * AdminEventsPage — unified event log across every conversion touchpoint.
 *
 * Merges sign_ups, hero leads, free leads, free-score leads, CTA
 * clicks, checkout events, and analyses into one time-sorted table.
 * Same person appears once per event.
 *
 * Auth: admin OR editor role — both roles can view + export.
 *
 * Features:
 *   - Global date range + event-type multi-filter + free-text search
 *   - Pagination (500 per page by default, up to 5000)
 *   - CSV export of the current query
 *   - "Copy shareable link" that preserves the current filters in the
 *     URL — any teammate with admin/editor role gets the same view
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, RefreshCw, Loader2, Download, Share2, Check, Search,
  Filter, ExternalLink,
} from 'lucide-react';
import { AppShell } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { api } from '../lib/api';

const ALL_EVENT_TYPES = [
  { key: 'sign_up',            label: 'Sign up',            tone: 'emerald' },
  { key: 'hero_lead',          label: 'Hero lead',          tone: 'sky' },
  { key: 'free_lead',          label: 'Free lead',          tone: 'violet' },
  { key: 'free_score_lead',    label: 'Free-score lead',    tone: 'indigo' },
  { key: 'cta_click',          label: 'CTA click',          tone: 'slate' },
  { key: 'checkout_open',      label: 'Checkout open',      tone: 'amber' },
  { key: 'purchase',           label: 'Purchase',           tone: 'emerald' },
  { key: 'checkout_abandoned', label: 'Abandoned',          tone: 'red' },
  { key: 'analysis_started',   label: 'Analysis started',   tone: 'sky' },
  { key: 'analysis_completed', label: 'Analysis complete',  tone: 'emerald' },
];

const TONE_MAP = {
  emerald: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/12 dark:text-emerald-300 border-emerald-200/60',
  sky:     'bg-sky-50 text-sky-700 dark:bg-sky-500/12 dark:text-sky-300 border-sky-200/60',
  violet:  'bg-violet-50 text-violet-700 dark:bg-violet-500/12 dark:text-violet-300 border-violet-200/60',
  indigo:  'bg-indigo-50 text-indigo-700 dark:bg-indigo-500/12 dark:text-indigo-300 border-indigo-200/60',
  slate:   'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 border-slate-200/60',
  amber:   'bg-amber-50 text-amber-700 dark:bg-amber-500/12 dark:text-amber-300 border-amber-200/60',
  red:     'bg-red-50 text-red-700 dark:bg-red-500/12 dark:text-red-400 border-red-200/60',
};

const PAGE_SIZE = 100;

function istToday() {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
}
function istDaysAgo(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata', year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
}


function EventPill({ event }) {
  const meta = ALL_EVENT_TYPES.find((e) => e.key === event);
  const tone = meta?.tone || 'slate';
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium whitespace-nowrap ${TONE_MAP[tone]}`}
      data-testid={`event-pill-${event}`}
    >
      {meta?.label || event}
    </span>
  );
}


export default function AdminEventsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [range, setRange] = useState({
    start: searchParams.get('start') || istDaysAgo(30),
    end: searchParams.get('end') || istToday(),
  });
  const [selectedEvents, setSelectedEvents] = useState(() => {
    const raw = searchParams.get('event') || '';
    return new Set(raw ? raw.split(',').filter(Boolean) : []);
  });
  const [searchQ, setSearchQ] = useState(searchParams.get('q') || '');
  const [debouncedQ, setDebouncedQ] = useState(searchQ);
  const [page, setPage] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Debounce free-text search so we don't hammer the endpoint on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(searchQ), 400);
    return () => clearTimeout(t);
  }, [searchQ]);

  // Sync current filter state back into the URL so refresh/back preserve
  // state AND the "Share link" button just needs `location.href`.
  useEffect(() => {
    const p = {};
    if (range.start) p.start = range.start;
    if (range.end) p.end = range.end;
    if (selectedEvents.size > 0) p.event = [...selectedEvents].join(',');
    if (debouncedQ) p.q = debouncedQ;
    setSearchParams(p, { replace: true });
  }, [range.start, range.end, selectedEvents, debouncedQ, setSearchParams]);

  const load = useCallback(async () => {
    if (!range.start || !range.end) return;
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({
        start: range.start,
        end: range.end,
        limit: String(PAGE_SIZE),
        skip: String(page * PAGE_SIZE),
      });
      if (selectedEvents.size > 0) params.set('event', [...selectedEvents].join(','));
      if (debouncedQ) params.set('q', debouncedQ);
      const r = await api.get(`/admin/events?${params.toString()}`);
      setData(r.data);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Failed to load events.';
      setError(typeof msg === 'string' ? msg : 'Failed to load events.');
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        setError('You need admin or editor access to view this page.');
      }
    } finally {
      setLoading(false);
    }
  }, [range.start, range.end, page, selectedEvents, debouncedQ]);

  useEffect(() => { load(); }, [load]);

  // Reset pagination when filters change.
  useEffect(() => { setPage(0); }, [range.start, range.end, debouncedQ, selectedEvents]);

  const toggleEvent = (key) => {
    setSelectedEvents((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const downloadCsv = async () => {
    // Build URL with current filters + token (backend accepts Bearer only,
    // so we fetch as blob + trigger download client-side).
    try {
      const params = new URLSearchParams({ start: range.start, end: range.end });
      if (selectedEvents.size > 0) params.set('event', [...selectedEvents].join(','));
      if (debouncedQ) params.set('q', debouncedQ);
      const r = await api.get(`/admin/events.csv?${params.toString()}`, { responseType: 'blob' });
      const blob = new Blob([r.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `blayzrr-events-${range.start}-to-${range.end}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
    } catch (err) {
      toast.error(err?.message || 'Failed to download CSV');
    }
  };

  const shareLink = async () => {
    // Encode current filters into a shareable link. Recipient must be
    // logged in as admin OR editor to view it.
    try {
      const href = window.location.href;
      await navigator.clipboard.writeText(href);
      toast.success('Link copied. Only admins and editors can open it.');
    } catch {
      toast.error('Could not copy — please copy the URL from the address bar.');
    }
  };

  const totalPages = useMemo(() => {
    if (!data) return 0;
    return Math.ceil((data.total || 0) / PAGE_SIZE);
  }, [data]);

  const items = data?.items || [];
  const counts = data?.counts_by_event || {};

  return (
    <AppShell>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 py-6 sm:py-8" data-testid="admin-events-page">
        {/* Header */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin')} className="rounded-full" data-testid="events-back">
            <ArrowLeft size={14} className="mr-1.5" /> Admin
          </Button>
          <div>
            <p className="text-[10.5px] font-mono-edge tracking-[0.22em] uppercase text-muted-foreground">Unified log</p>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-slate-50 leading-tight">
              All events · one timeline
            </h1>
          </div>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <Button size="sm" variant="outline" onClick={shareLink} className="rounded-full" data-testid="events-share">
              <Share2 size={13} className="mr-1.5" /> Share link
            </Button>
            <Button size="sm" variant="outline" onClick={downloadCsv} className="rounded-full" data-testid="events-export-csv">
              <Download size={13} className="mr-1.5" /> Export CSV
            </Button>
            <Button size="sm" onClick={load} disabled={loading} className="rounded-full" data-testid="events-refresh">
              {loading ? <Loader2 size={13} className="animate-spin mr-1.5" /> : <RefreshCw size={13} className="mr-1.5" />}
              Refresh
            </Button>
          </div>
        </div>

        {/* Filter bar */}
        <Card className="border border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-950 rounded-2xl shadow-sm" data-testid="events-filter-bar">
          <CardContent className="p-4 sm:p-5">
            <div className="flex flex-wrap items-end gap-3">
              <div className="flex flex-col gap-1">
                <label className="text-[10.5px] font-mono-edge tracking-[0.14em] uppercase text-muted-foreground">Start · IST</label>
                <input
                  type="date"
                  value={range.start}
                  onChange={(e) => setRange((r) => ({ ...r, start: e.target.value }))}
                  className="rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-2.5 py-1.5 text-[13px] focus:ring-2 focus:ring-primary/40 focus:outline-none"
                  data-testid="events-start"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-[10.5px] font-mono-edge tracking-[0.14em] uppercase text-muted-foreground">End · IST</label>
                <input
                  type="date"
                  value={range.end}
                  onChange={(e) => setRange((r) => ({ ...r, end: e.target.value }))}
                  className="rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-2.5 py-1.5 text-[13px] focus:ring-2 focus:ring-primary/40 focus:outline-none"
                  data-testid="events-end"
                />
              </div>
              <div className="flex flex-col gap-1 flex-1 min-w-[220px]">
                <label className="text-[10.5px] font-mono-edge tracking-[0.14em] uppercase text-muted-foreground">Search</label>
                <div className="relative">
                  <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    value={searchQ}
                    onChange={(e) => setSearchQ(e.target.value)}
                    placeholder="email, name, LinkedIn URL or phone…"
                    className="w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 pl-8 pr-3 py-1.5 text-[13px] focus:ring-2 focus:ring-primary/40 focus:outline-none"
                    data-testid="events-search"
                  />
                </div>
              </div>
            </div>

            {/* Event-type pills — click to filter. */}
            <div className="mt-3 flex flex-wrap items-center gap-1.5">
              <span className="inline-flex items-center gap-1 text-[10.5px] font-mono-edge tracking-[0.14em] uppercase text-muted-foreground mr-1">
                <Filter size={11} /> Events
              </span>
              {ALL_EVENT_TYPES.map((et) => {
                const isOn = selectedEvents.has(et.key);
                const count = counts[et.key] || 0;
                return (
                  <button
                    key={et.key}
                    type="button"
                    onClick={() => toggleEvent(et.key)}
                    className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11.5px] transition-all ${
                      isOn
                        ? `${TONE_MAP[et.tone]} ring-2 ring-offset-1 ring-primary/30`
                        : 'border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-900'
                    }`}
                    data-testid={`events-filter-${et.key}`}
                  >
                    {isOn && <Check size={10} />}
                    {et.label}
                    <span className="tabular-nums opacity-70">{count}</span>
                  </button>
                );
              })}
              {selectedEvents.size > 0 && (
                <button
                  type="button"
                  onClick={() => setSelectedEvents(new Set())}
                  className="ml-2 text-[11.5px] text-primary hover:underline"
                  data-testid="events-clear-filters"
                >
                  clear
                </button>
              )}
            </div>
          </CardContent>
        </Card>

        {error && (
          <div className="mt-5 rounded-xl border border-red-300/60 bg-red-50 dark:bg-red-950/20 dark:border-red-900/50 text-red-700 dark:text-red-300 px-4 py-3 text-[13.5px]" data-testid="events-error">
            {error}
          </div>
        )}

        {/* Results */}
        <div className="mt-5" data-testid="events-results">
          <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
            <p className="text-[13px] text-muted-foreground">
              {data ? (
                <>
                  Showing{' '}
                  <span className="font-semibold text-slate-800 dark:text-slate-200 tabular-nums">
                    {items.length === 0 ? 0 : (page * PAGE_SIZE) + 1}–{(page * PAGE_SIZE) + items.length}
                  </span>
                  {' of '}
                  <span className="font-semibold text-slate-800 dark:text-slate-200 tabular-nums">{data.total}</span>
                  {' events'}
                </>
              ) : (loading ? 'Loading…' : ' ')}
            </p>
            {totalPages > 1 && (
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))} className="h-8 rounded-full" data-testid="events-prev">
                  ← Prev
                </Button>
                <span className="text-[12px] text-muted-foreground tabular-nums">Page {page + 1} of {totalPages}</span>
                <Button size="sm" variant="outline" disabled={page >= totalPages - 1} onClick={() => setPage((p) => p + 1)} className="h-8 rounded-full" data-testid="events-next">
                  Next →
                </Button>
              </div>
            )}
          </div>

          <Card className="border border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-950 rounded-2xl shadow-sm overflow-hidden" data-testid="events-table-card">
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]" data-testid="events-table">
                <thead className="bg-slate-50 dark:bg-slate-900/60 text-left border-b border-slate-200/80 dark:border-slate-800">
                  <tr>
                    <Th>Day · Time (IST)</Th>
                    <Th>Event</Th>
                    <Th>Name</Th>
                    <Th>Email</Th>
                    <Th>LinkedIn</Th>
                    <Th>Referral</Th>
                    <Th>Phone</Th>
                    <Th>Plan</Th>
                    <Th>Razorpay ID</Th>
                    <Th>IP</Th>
                    <Th className="text-right">Analyses</Th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {items.map((r, i) => (
                    <tr key={i} className="hover:bg-slate-50 dark:hover:bg-slate-900/40 transition-colors" data-testid={`events-row-${i}`}>
                      <Td>
                        <div className="whitespace-nowrap tabular-nums">
                          <div className="text-slate-800 dark:text-slate-100 font-medium">{r.day || '—'}</div>
                          <div className="text-[11px] text-slate-500">{r.time || ''}</div>
                        </div>
                      </Td>
                      <Td><EventPill event={r.event} /></Td>
                      <Td className="max-w-[160px] truncate" title={r.name}>{r.name || '—'}</Td>
                      <Td className="max-w-[220px] truncate" title={r.email}>{r.email || '—'}</Td>
                      <Td className="max-w-[200px] truncate">
                        {r.linkedin_url ? (
                          <a href={r.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline inline-flex items-center gap-1">
                            {r.linkedin_url.replace(/^https?:\/\//, '').slice(0, 30)}
                            <ExternalLink size={10} />
                          </a>
                        ) : '—'}
                      </Td>
                      <Td className="max-w-[180px] truncate text-slate-500" title={r.referral}>{r.referral || '—'}</Td>
                      <Td className="text-slate-600 whitespace-nowrap">{r.phone || '—'}</Td>
                      <Td>{r.plan ? <Badge variant="secondary" className="text-[10.5px] uppercase">{r.plan}</Badge> : '—'}</Td>
                      <Td className="text-[11px] text-slate-500 font-mono max-w-[140px] truncate" title={r.razorpay_id}>{r.razorpay_id || '—'}</Td>
                      <Td className="text-[11px] text-slate-500 whitespace-nowrap">{r.ip || '—'}</Td>
                      <Td className="text-right tabular-nums font-semibold">{r.analyses ?? 0}</Td>
                    </tr>
                  ))}
                  {!loading && items.length === 0 && (
                    <tr>
                      <td colSpan={11} className="text-center py-10 text-[13px] text-slate-500 italic">
                        No events match these filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              {loading && items.length === 0 && (
                <div className="flex items-center justify-center py-10 text-[13px] text-slate-500 gap-2">
                  <Loader2 size={14} className="animate-spin" /> Loading events…
                </div>
              )}
            </div>
          </Card>
        </div>

        {/* Small footer tip about API surface — helps engineers debug data issues. */}
        <p className="mt-3 text-[11px] text-slate-500">
          Data source: <code>GET /api/admin/events</code> · CSV: <code>GET /api/admin/events.csv</code>
        </p>
      </div>
    </AppShell>
  );
}


function Th({ children, className = '' }) {
  return (
    <th className={`px-3 py-2.5 text-[10.5px] font-mono-edge tracking-[0.16em] uppercase text-muted-foreground ${className}`}>
      {children}
    </th>
  );
}
function Td({ children, className = '' }) {
  return <td className={`px-3 py-2.5 align-top text-slate-700 dark:text-slate-200 ${className}`}>{children}</td>;
}
